import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Key as KeyIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import userService from '../../../services/userService';
import { AdminUser } from '../../../services/authService';
import venueService from '../../../services/venueService';
import type { Venue } from '../../../types/venue';

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [assignedVenues, setAssignedVenues] = useState<Venue[]>([]);
  const { isSystemAdmin } = useAuth();
  const navigate = useNavigate();

  // Fetch user details on component mount
  useEffect(() => {
    const fetchUserDetails = async () => {
      console.log('UserDetail component - userId from params:', userId);
      
      if (!userId) {
        console.error('UserDetail component - userId is undefined or null');
        setError('User ID is missing. Please go back to the user list and try again.');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        console.log('Fetching user details for ID:', userId);
        const data = await userService.getUserById(userId);
        console.log('Received user data:', data);
        setUser(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch user details:', err);
        setError('Failed to load user details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId]);

  // Resolve assigned venue ids to venue objects so we can show names rather
  // than opaque ObjectIds. Only fires for VENUE_USER with at least one id.
  useEffect(() => {
    if (!user || user.role !== 'VENUE_USER' || !user.assignedVenueIds || user.assignedVenueIds.length === 0) {
      setAssignedVenues([]);
      return;
    }
    let cancelled = false;
    Promise.all(user.assignedVenueIds.map((id) => venueService.getVenue(id).catch(() => null)))
      .then((results) => {
        if (!cancelled) {
          setAssignedVenues(results.filter((v): v is Venue => v !== null));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Redirect if not system admin
  useEffect(() => {
    if (!isSystemAdmin()) {
      navigate('/admin/dashboard');
    }
  }, [isSystemAdmin, navigate]);

  const handleBack = () => {
    navigate('/admin/users', { replace: true });
  };

  const handleEdit = () => {
    if (userId) {
      navigate(`/admin/users/${userId}/edit`);
    }
  };

  const handleResetPassword = () => {
    if (userId) {
      navigate(`/admin/users/${userId}/reset-password`);
    }
  };

  const handleDelete = async () => {
    if (!userId) return;

    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userService.deleteUser(userId);
        navigate('/admin/users');
      } catch (err) {
        console.error('Failed to delete user:', err);
        alert('Failed to delete user. Please try again later.');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography color="error" variant="h6">
            {error || 'User not found'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Back to Users
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={handleBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              User Details
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Reset Password">
              <IconButton 
                color="primary" 
                onClick={handleResetPassword}
                sx={{ mr: 1 }}
              >
                <KeyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit User">
              <IconButton 
                color="primary" 
                onClick={handleEdit}
                sx={{ mr: 1 }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete User">
              <IconButton 
                color="error" 
                onClick={handleDelete}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CardHeader title="Basic Information" />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      First Name
                    </Typography>
                    <Typography variant="body1">
                      {user.firstName}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Last Name
                    </Typography>
                    <Typography variant="body1">
                      {user.lastName}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {user.email}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Role
                    </Typography>
                    <Chip
                      label={
                        user.role === 'SYSTEM_ADMIN'
                          ? 'System Admin'
                          : user.role === 'VENUE_USER'
                            ? 'Venue User'
                            : 'Admin'
                      }
                      color={
                        user.role === 'SYSTEM_ADMIN'
                          ? 'secondary'
                          : user.role === 'VENUE_USER'
                            ? 'info'
                            : 'primary'
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip 
                      label={user.active ? 'Active' : 'Inactive'} 
                      color={user.active ? 'success' : 'error'} 
                      size="small" 
                    />
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Password Change Required
                    </Typography>
                    <Chip 
                      label={user.passwordChangeRequired ? 'Yes' : 'No'} 
                      color={user.passwordChangeRequired ? 'warning' : 'success'} 
                      size="small" 
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {user.role === 'VENUE_USER' && (
            <Grid component="div" sx={{ gridColumn: { xs: 'span 12' } }}>
              <Card
                sx={{
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}
              >
                <CardHeader title={`Assigned Venues (${user.assignedVenueIds?.length ?? 0})`} />
                <Divider />
                <CardContent>
                  {(user.assignedVenueIds ?? []).length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No venues assigned. This user cannot access any venue workspace.
                    </Typography>
                  ) : assignedVenues.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      Loading venues…
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {assignedVenues.map((v) => (
                        <Chip key={v._id} label={v.name} size="small" />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CardHeader title="Account Information" />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      User ID
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                      {user._id}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Created At
                    </Typography>
                    <Typography variant="body1">
                      {new Date(user.createdAt).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body1">
                      {new Date(user.updatedAt).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default UserDetail;
